import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Scale } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FISCAL_LEGAL = "Simulación orientativa basada en la normativa del IRPF y del Impuesto sobre Sociedades vigente a octubre de 2026. No incluye tributación autonómica distinta, plusvalía municipal ni circunstancias particulares. No constituye asesoramiento fiscal personalizado.";

const parseNum = (s) => Number(String(s).replace(/[.\s]/g, "").replace(",", "."));
const fmt = (n) => new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(n);

const inputCls =
  "w-full border border-gold/20 bg-white/[0.04] px-4 py-3.5 text-sm text-white outline-none transition-all placeholder:text-slate-500 focus:border-gold focus:ring-1 focus:ring-gold/40";

const Field = ({ label, hint, children }) => (
  <div>
    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">{label}</label>
    {children}
    {hint ? <p className="mt-2 text-xs leading-relaxed text-slate-500">{hint}</p> : null}
  </div>
);

const ChoiceGroup = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2.5" data-testid={testid}>
    {options.map((o) => (
      <button
        key={o.v}
        type="button"
        data-testid={`${testid}-${o.v}`}
        onClick={() => onChange(o.v)}
        className={`border px-4 py-2.5 text-sm font-medium transition-all duration-300 ${
          value === o.v
            ? "border-gold bg-gold text-ink"
            : "border-gold/25 bg-white/[0.03] text-slate-300 hover:border-gold/60 hover:text-gold"
        }`}
      >
        {o.l}
      </button>
    ))}
  </div>
);

const ScenarioCard = ({ tag, tagGold, title, rows, children, testid }) => (
  <div data-testid={testid} className="relative border border-gold/20 bg-ink-3/60 p-7">
    <div className="pointer-events-none absolute -left-2 -top-2 h-5 w-5 border-l border-t border-gold" />
    <span className={`text-[10px] font-semibold uppercase tracking-[0.22em] ${tagGold ? "text-gold" : "text-slate-400"}`}>
      {tag}
    </span>
    <h3 className="mt-2 font-serif text-2xl text-white">{title}</h3>
    <dl className="mt-5 space-y-3">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-baseline justify-between gap-4 border-b border-gold/10 pb-3">
          <dt className="text-sm text-slate-400">{k}</dt>
          <dd className="font-serif text-xl text-gold-light">{v}</dd>
        </div>
      ))}
    </dl>
    {children}
  </div>
);

export default function FiscalCalculator() {
  const [form, setForm] = useState({
    precio_venta: "", valor_adquisicion: "", gastos: "", titular: "persona_fisica",
    pct_participacion: "", antiguedad: "", pre_1994: "", necesita_dinero: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lead, setLead] = useState({ nombre: "", email: "", telefono: "", consent_privacidad: false, consent_comercial: false });
  const [leadDone, setLeadDone] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);

  useEffect(() => {
    document.title = "Calculadora fiscal de la venta de tu empresa | AJG M&A Consulting";
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const payload = () => ({
    precio_venta: parseNum(form.precio_venta),
    valor_adquisicion: parseNum(form.valor_adquisicion),
    gastos: form.gastos ? parseNum(form.gastos) : 0,
    titular: form.titular,
    pct_participacion: parseNum(form.pct_participacion),
    antiguedad: form.antiguedad,
    pre_1994: form.pre_1994 === "si",
    necesita_dinero: form.necesita_dinero === "si",
  });

  const simulate = async (e) => {
    e.preventDefault();
    const p = payload();
    if (!(p.precio_venta > 0) || p.valor_adquisicion < 0 || Number.isNaN(p.valor_adquisicion) ||
        !(p.pct_participacion >= 0 && p.pct_participacion <= 100) || !form.antiguedad || !form.pre_1994 || !form.necesita_dinero) {
      toast.error("Completa todos los campos obligatorios.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/fiscal/simulate`, p);
      setResult(data);
      setLeadDone(false);
    } catch (err) {
      if (err.response?.status === 429) toast.error("Demasiadas peticiones. Inténtalo más tarde.");
      else toast.error("No se pudo simular. Revisa los datos.");
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.consent_privacidad) {
      toast.error("Debes aceptar la política de privacidad.");
      return;
    }
    setLeadLoading(true);
    try {
      await axios.post(`${API}/fiscal/lead`, {
        nombre: lead.nombre, email: lead.email, telefono: lead.telefono,
        consent_privacidad: lead.consent_privacidad, consent_comercial: lead.consent_comercial,
        simulacion: payload(),
      });
      setLeadDone(true);
    } catch {
      toast.error("No se pudo enviar. Inténtalo de nuevo.");
    } finally {
      setLeadLoading(false);
    }
  };

  const esPersona = form.titular === "persona_fisica";
  const a = result?.escenario_a;
  const b = result?.escenario_b;

  return (
    <div className="min-h-screen">
      <header className="border-b border-gold/10">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-6 md:px-10">
          <Link to="/" data-testid="fiscal-logo">
            <img src="/logo-light.png" alt="AJG M&A Consulting" className="h-12 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="fiscal-back"
            className="group flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-300 transition-colors hover:text-gold"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            Volver al inicio
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24" data-testid="fiscal-page">
        <p className="eyebrow">Herramientas · Calculadora fiscal</p>
        <h1 className="mt-6 max-w-3xl font-serif text-4xl leading-tight text-white sm:text-5xl">
          ¿Cuánto pagarás de impuestos por vender tu empresa?
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">
          Simula la tributación de la venta de tus participaciones como persona física y compárala con el escenario
          de una sociedad holding. El cómo estructurarlo lo analizamos contigo de forma confidencial.
        </p>

        <div className="mt-14 grid gap-12 lg:grid-cols-2 lg:items-start">
          <form onSubmit={simulate} data-testid="fiscal-form" className="space-y-6">
            <Field label="Precio de venta estimado de las participaciones (€)">
              <input data-testid="fiscal-precio" value={form.precio_venta} onChange={set("precio_venta")} inputMode="numeric" placeholder="Ej.: 800.000" className={inputCls} />
            </Field>
            <Field label="Valor de adquisición (capital aportado o precio pagado) (€)">
              <input data-testid="fiscal-adquisicion" value={form.valor_adquisicion} onChange={set("valor_adquisicion")} inputMode="numeric" placeholder="Ej.: 100.000" className={inputCls} />
            </Field>
            <Field label="Gastos de la operación (asesores, notaría) (€)" hint="Opcional, por defecto 0">
              <input data-testid="fiscal-gastos" value={form.gastos} onChange={set("gastos")} inputMode="numeric" placeholder="0" className={inputCls} />
            </Field>
            <Field label="¿Quién es el titular de las participaciones?">
              <ChoiceGroup
                testid="fiscal-titular"
                options={[{ v: "persona_fisica", l: "Persona física" }, { v: "holding", l: "Sociedad holding" }]}
                value={form.titular}
                onChange={(v) => setForm({ ...form, titular: v })}
              />
            </Field>
            <Field label="% de participación en la empresa">
              <input data-testid="fiscal-pct" value={form.pct_participacion} onChange={set("pct_participacion")} inputMode="numeric" placeholder="Ej.: 100" className={inputCls} />
            </Field>
            <Field label="Antigüedad de la participación">
              <ChoiceGroup
                testid="fiscal-antiguedad"
                options={[{ v: "<1", l: "Menos de 1 año" }, { v: ">=1", l: "1 año o más" }]}
                value={form.antiguedad}
                onChange={(v) => setForm({ ...form, antiguedad: v })}
              />
            </Field>
            <Field label="¿Las participaciones se adquirieron antes del 31/12/1994?">
              <ChoiceGroup
                testid="fiscal-1994"
                options={[{ v: "no", l: "No" }, { v: "si", l: "Sí" }]}
                value={form.pre_1994}
                onChange={(v) => setForm({ ...form, pre_1994: v })}
              />
            </Field>
            <Field label="¿Necesitas disponer personalmente del dinero?">
              <ChoiceGroup
                testid="fiscal-dinero"
                options={[{ v: "no", l: "No" }, { v: "si", l: "Sí" }]}
                value={form.necesita_dinero}
                onChange={(v) => setForm({ ...form, necesita_dinero: v })}
              />
            </Field>
            <button
              type="submit"
              disabled={loading}
              data-testid="fiscal-simulate-button"
              className="flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-all duration-300 hover:bg-gold-light disabled:opacity-60"
            >
              {loading ? "Calculando..." : "Simular tributación"}
              <ArrowRight className="h-4 w-4" />
            </button>
            <p className="text-xs leading-relaxed text-slate-500">{FISCAL_LEGAL}</p>
          </form>

          <div className="lg:sticky lg:top-10">
            <AnimatePresence mode="wait">
              {!result ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="relative border border-gold/20 bg-ink-3/60 p-8"
                  data-testid="fiscal-empty"
                >
                  <span className="flex h-12 w-12 items-center justify-center border border-gold/40">
                    <Scale className="h-6 w-6 text-gold" />
                  </span>
                  <p className="mt-5 text-sm leading-relaxed text-slate-400">
                    Completa los datos y simula para comparar ambos escenarios: persona física frente a sociedad
                    holding.
                  </p>
                </motion.div>
              ) : (
                <motion.div key="result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                  {!result.aplicable ? (
                    <div className="border border-gold/20 bg-ink-3/60 p-8" data-testid="fiscal-nogain">
                      <p className="font-serif text-2xl text-white">
                        No se genera ganancia patrimonial sujeta a tributación.
                      </p>
                      <p className="mt-3 text-sm text-slate-300">
                        Ganancia resultante: {fmt(result.ganancia)} €
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-8" data-testid="fiscal-result">
                      <div className="grid gap-6 sm:grid-cols-2">
                        <ScenarioCard
                          testid="fiscal-scenario-a"
                          tag={esPersona ? "Tu situación actual" : "Comparación"}
                          tagGold={esPersona}
                          title="Persona física (IRPF)"
                          rows={[
                            ["Ganancia patrimonial", `${fmt(result.ganancia)} €`],
                            ["Cuota IRPF", `${fmt(a.cuota)} €`],
                            ["Tipo efectivo medio", `${String(a.tipo_efectivo).replace(".", ",")} %`],
                            ["Neto disponible", `${fmt(a.neto)} €`],
                          ]}
                        />
                        <ScenarioCard
                          testid="fiscal-scenario-b"
                          tag={esPersona ? "Escenario ilustrativo" : "Tu situación actual"}
                          tagGold={!esPersona}
                          title="Sociedad holding (IS)"
                          rows={[
                            ["Exención art. 21 LIS", b.exencion ? "Aplicable (95 %)" : "No aplicable"],
                            ["Cuota IS", `${fmt(b.cuota)} €`],
                            ["Tipo efectivo", `${String(b.tipo_efectivo).replace(".", ",")} %`],
                            ["Neto en la holding", `${fmt(b.neto)} €`],
                          ]}
                        >
                          {b.dividendo && (
                            <div className="mt-5 border-t border-gold/10 pt-4" data-testid="fiscal-dividendo">
                              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">
                                Si distribuyes el dividendo
                              </p>
                              <dl className="mt-3 space-y-2">
                                <div className="flex justify-between text-sm">
                                  <dt className="text-slate-400">Tributación del dividendo</dt>
                                  <dd className="text-white">{fmt(b.dividendo.cuota_dividendo)} €</dd>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <dt className="text-slate-400">Neto final del socio</dt>
                                  <dd className="text-white">{fmt(b.dividendo.neto_socio)} €</dd>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <dt className="text-slate-400">Impuestos totales combinados</dt>
                                  <dd className="font-semibold text-gold-light">{fmt(b.dividendo.total_impuestos)} €</dd>
                                </div>
                              </dl>
                            </div>
                          )}
                        </ScenarioCard>
                      </div>

                      <div className="border border-gold/20 bg-ink-3/60 p-7" data-testid="fiscal-chart">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Comparativa</p>
                        {[
                          { label: "Persona física", cuota: a.cuota, neto: a.neto },
                          { label: "Sociedad holding", cuota: b.dividendo ? b.dividendo.total_impuestos : b.cuota, neto: b.dividendo ? b.dividendo.neto_socio : b.neto },
                        ].map((s) => (
                          <div key={s.label} className="mt-6">
                            <p className="mb-2 text-sm text-slate-300">{s.label}</p>
                            <div className="space-y-2">
                              <div className="flex items-center gap-3">
                                <div className="h-3.5 bg-gold-dark transition-all duration-700" style={{ width: `${(s.cuota / result.ganancia) * 100}%`, minWidth: "4px" }} />
                                <span className="text-xs text-slate-400">Impuestos: {fmt(s.cuota)} €</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="h-3.5 bg-gold transition-all duration-700" style={{ width: `${(s.neto / result.ganancia) * 100}%`, minWidth: "4px" }} />
                                <span className="text-xs text-slate-400">Neto: {fmt(s.neto)} €</span>
                              </div>
                            </div>
                          </div>
                        ))}
                        <p className="mt-8 font-serif text-3xl text-white">
                          Diferencia fiscal potencial:{" "}
                          <span className="italic text-gold" data-testid="fiscal-diferencia">{fmt(result.diferencia)} €</span>
                        </p>
                      </div>

                      <div className="border-l-2 border-gold/50 bg-gold/[0.05] p-6" data-testid="fiscal-diferimiento">
                        <p className="text-sm leading-relaxed text-slate-300">
                          El ahorro del escenario holding se mantiene mientras el dinero permanezca en la sociedad
                          (por ejemplo, para reinvertir). Si se distribuye al socio como dividendo, tributa en su
                          IRPF como rendimiento del ahorro.
                        </p>
                      </div>

                      {result.aviso_1994 && (
                        <div className="border border-gold/30 bg-ink-3/60 p-6" data-testid="fiscal-aviso-1994">
                          <p className="text-sm leading-relaxed text-slate-200">
                            Pueden existir reducciones adicionales por régimen transitorio. Consúltanos.
                          </p>
                        </div>
                      )}

                      {esPersona && (
                        <div className="border border-gold/40 bg-ink-3/60 p-7" data-testid="fiscal-warning">
                          <p className="text-sm leading-relaxed text-slate-200">
                            Crear una holding inmediatamente antes de la venta puede no producir este ahorro e
                            implica riesgos de regularización por la Agencia Tributaria. La viabilidad depende de
                            los plazos y del motivo económico de la reestructuración.{" "}
                            <strong className="text-white">Analizamos tu caso de forma confidencial.</strong>
                          </p>
                          <button
                            onClick={() => document.getElementById("fiscal-lead")?.scrollIntoView({ behavior: "smooth" })}
                            data-testid="fiscal-analisis-cta"
                            className="mt-5 flex items-center gap-3 border border-gold/60 px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
                          >
                            Solicitar análisis <ArrowRight className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {result?.aplicable && (
          <div id="fiscal-lead" className="mt-20 border-t border-gold/10 pt-16">
            <div className="mx-auto max-w-2xl">
              {leadDone ? (
                <div className="border border-gold/25 bg-ink-3/60 p-10 text-center" data-testid="fiscal-lead-done">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-gold" />
                  <p className="mt-6 font-serif text-3xl text-white">Hemos enviado el resumen a tu email.</p>
                  <p className="mt-4 text-sm leading-relaxed text-slate-300">
                    Un socio de AJG te contactará en 24-48 h para analizar tu caso de forma confidencial.
                  </p>
                </div>
              ) : (
                <form onSubmit={submitLead} data-testid="fiscal-lead-form" className="space-y-5">
                  <h2 className="font-serif text-3xl text-white">¿Quieres optimizar la venta?</h2>
                  <p className="text-sm leading-relaxed text-slate-300">
                    Déjanos tus datos y recibirás por email el resumen de tu simulación. Te contactaremos para
                    estudiar cómo estructurar la operación.
                  </p>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Nombre">
                      <input data-testid="fiscal-nombre" value={lead.nombre} onChange={(e) => setLead({ ...lead, nombre: e.target.value })} required className={inputCls} />
                    </Field>
                    <Field label="Teléfono">
                      <input data-testid="fiscal-telefono" type="tel" value={lead.telefono} onChange={(e) => setLead({ ...lead, telefono: e.target.value })} required className={inputCls} />
                    </Field>
                  </div>
                  <Field label="Email">
                    <input data-testid="fiscal-email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} required className={inputCls} />
                  </Field>
                  <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-slate-300">
                    <input type="checkbox" checked={lead.consent_privacidad} onChange={(e) => setLead({ ...lead, consent_privacidad: e.target.checked })} className="mt-1 h-4 w-4 shrink-0 accent-gold" data-testid="fiscal-check-privacy" />
                    <span>
                      He leído y acepto la{" "}
                      <Link to="/privacidad" className="text-gold underline underline-offset-2">política de privacidad</Link>. *
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-slate-300">
                    <input type="checkbox" checked={lead.consent_comercial} onChange={(e) => setLead({ ...lead, consent_comercial: e.target.checked })} className="mt-1 h-4 w-4 shrink-0 accent-gold" data-testid="fiscal-check-commercial" />
                    <span>Acepto recibir comunicaciones comerciales de AJG M&amp;A Consulting.</span>
                  </label>
                  <button
                    type="submit"
                    disabled={leadLoading}
                    data-testid="fiscal-lead-submit"
                    className="flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-all duration-300 hover:bg-gold-light disabled:opacity-60"
                  >
                    {leadLoading ? "Enviando..." : "Quiero analizar cómo optimizar la venta"}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </form>
              )}
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-gold/10 py-8">
        <p className="mx-auto max-w-7xl px-6 text-xs tracking-wide text-slate-500 md:px-10">
          © 2026 AJG M&amp;A Consulting, S.L. Todos los derechos reservados.
        </p>
      </footer>
    </div>
  );
}
